import type { AgentStatus, RunStatus } from "./types";

/** What we know about an agent right now, in the order the status is decided. */
export type AgentStatusFacts = {
  enabled: boolean;
  /** Runs queued or running. */
  activeRuns: number;
  /** Questions and proposals waiting on a person. */
  pendingApprovals: number;
  budgetReached: boolean;
  lastRunStatus: RunStatus | null;
  /** Enabled schedules, i.e. the agent wakes itself up. */
  enabledSchedules: number;
  nextRunAt: string | null;
};

const EMPTY: AgentStatusFacts = {
  enabled: true, activeRuns: 0, pendingApprovals: 0, budgetReached: false, lastRunStatus: null, enabledSchedules: 0, nextRunAt: null,
};

/**
 * One status per agent, most urgent first: switched off, working, held up by a person or a budget,
 * broken, waking itself up on a schedule, or waiting to be asked.
 */
export function agentStatus(facts: Partial<AgentStatusFacts>): AgentStatus {
  const f = { ...EMPTY, ...facts };
  if (!f.enabled) return "disabled";
  if (f.activeRuns > 0) return "thinking";
  if (f.pendingApprovals > 0) return "waiting";
  if (f.budgetReached) return "blocked";
  if (f.lastRunStatus === "failed") return "failing";
  if (f.enabledSchedules > 0) return "scheduled";
  return "sleeping";
}

export const AGENT_STATUS_LABEL: Record<AgentStatus, string> = {
  disabled: "Disabled",
  thinking: "Thinking",
  waiting: "Waiting on you",
  blocked: "Over budget",
  failing: "Failing",
  scheduled: "Scheduled",
  sleeping: "Sleeping",
};

export const AGENT_STATUS_HINT: Record<AgentStatus, string> = {
  disabled: "Switched off. It runs nothing and takes no work.",
  thinking: "Working on something right now.",
  waiting: "It asked a question and cannot go on until someone answers.",
  blocked: "It reached its monthly budget, so new runs are refused until the cap is raised.",
  failing: "Its last run failed.",
  scheduled: "It wakes itself up on a schedule.",
  sleeping: "Nothing scheduled. It works when someone asks it or hands it a task.",
};
